import { InfoOutlined } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { useQueryClient } from '@tanstack/react-query'
import { memo, useCallback, useMemo } from 'react'
import Swal from 'sweetalert2'
import { useSuggestAddress } from '../../../../../hooks/suggest/use-suggest-address'
import { filterFilledParams } from '../../../../../hooks/use-search-params'
import { normalizeString } from '../../../../../utils/autocomplete'
import { ButtonPrimary, ButtonSecondaryOutlined } from '../../../../_ui/button'
import { InputStyled } from '../../../../_ui/input'
import useAddressPostData from '../../../hooks/use-address-post-data'
import { useAddAddressStore } from '../../../stores/add-address-store'
import { validateForm } from '../../../utils'
import {
  AddressFormActionStyled,
  AddressFormStyled,
  AddressInfoWrapper,
  AutocompleteStyled,
} from '../account-address-styled'
import { useEffect } from 'react'

const AddAddressForm = () => {
  const queryClient = useQueryClient()
  // Zustand store
  const form = useAddAddressStore(s => s.form)
  const setForm = useAddAddressStore(s => s.setForm)
  const closeModal = useAddAddressStore(s => s.closeModal)
  const onCloseComplete = useAddAddressStore(s => s.onCloseComplete)
  const clearOnCloseComplete = useAddAddressStore(s => s.clearOnCloseComplete)

  const handleOnSuccessUpdate = useCallback(() => {
    queryClient.invalidateQueries(['useMemberAddressData'])
    closeModal?.()
    onCloseComplete?.()
    clearOnCloseComplete?.()
  }, [queryClient, closeModal, onCloseComplete, clearOnCloseComplete])

  const { mutateAsync: postData, isLoading: isLoadingPostAddress } =
    useAddressPostData({
      onSuccess: handleOnSuccessUpdate,
    })

  const handleAddAddress = useCallback(() => {
    const {
      province_id,
      city_id,
      subdistrict_id,
      rajaOngkirId,
      provinceName,
      cityName,
      districtName,
      subdistrictName,
      ...rest
    } = form || {}

    // Data yang akan divalidasi
    const checkForm = {
      rajaOngkirId,
      provinceName,
      cityName,
      districtName,
      subdistrictName,
      ...rest,
    }

    console.log({ checkForm })

    // Validasi
    const isValid = validateForm(checkForm)
    if (!isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Data',
        text: 'Please fill out all required fields before submitting.',
      })
      return
    }

    // Payload untuk post
    const payload = {
      raja_ongkir_id: rajaOngkirId,
      province_id,
      city_id,
      subdistrict_id,
      province_name: provinceName,
      city_name: cityName,
      district_name: districtName,
      subdistrict_name: subdistrictName,
      ...rest,
    }

    return postData(payload)
  }, [form, postData])

  const queryParamsSuggest = useMemo(() => {
    const { cityName, districtName, subdistrictName } = form || {}

    const isLocationFilled = cityName || districtName || subdistrictName

    if (isLocationFilled) {
      return filterFilledParams({
        name: [subdistrictName, districtName, cityName]
          .filter(Boolean)
          .join(' '),
      })
    }

    return null
  }, [form])

  const {
    setSuggestKeyword,
    suggestAddress = [],
    isLoading: isSuggestLoading,
  } = useSuggestAddress(queryParamsSuggest)

  const disabled = isSuggestLoading || isLoadingPostAddress

  const handleChange = useCallback(
    e => {
      const { name, value } = e.target
      setForm(prev => ({ ...prev, [name]: value }))
    },
    [setForm]
  )

  const handleChangeKeyword = useCallback(
    e => {
      setSuggestKeyword(e.target.value)
    },
    [setSuggestKeyword]
  )

  const handleChangeAddress = useCallback(
    (e, v) => {
      if (v) {
        setForm(prev => {
          console.log({ prev })
          return {
            ...prev,
            city_id: v.cityId,
            postal_code: v.codePost,
            subdistrict_id: v.subdistrictId,
            province_id: v.provinceId,
            rajaOngkirId: v.rajaOngkirId,
            provinceName: v.provinceName,
            cityName: v.cityName,
            districtName: v.districtName,
            subdistrictName: v.subdistrictName,
          }
        })
      } else {
        setForm(prev => ({
          ...prev,
          city_id: null,
          postal_code: null,
          subdistrict_id: null,
          province_id: null,
          rajaOngkirId: null,
          provinceName: null,
          cityName: null,
          districtName: null,
          subdistrictName: null,
        }))
      }
    },
    [setForm]
  )

  const autoCompleteValue = useMemo(() => {
    if (!suggestAddress || !form?.rajaOngkirId) return null
    return (
      suggestAddress.find(
        item => item?.rajaOngkirId?.toString() === form.rajaOngkirId?.toString()
      ) || null
    )
  }, [suggestAddress, form?.rajaOngkirId])

  return (
    <AddressFormStyled>
      <InputStyled
        autoFocus
        required
        fullWidth
        name='name'
        label='Name'
        variant='outlined'
        size='small'
        InputLabelProps={{ shrink: true }}
        onChange={handleChange}
        value={form?.name}
      />
      <InputStyled
        required
        fullWidth
        name='phone'
        label='No. Ponsel'
        variant='outlined'
        size='small'
        type='number'
        InputLabelProps={{ shrink: true }}
        onChange={handleChange}
        value={form?.phone}
      />
      <AddressInfoWrapper>
        <InfoOutlined />
        <span>
          Pastikan setelah input <strong>Kota / Kecamatan</strong> anda. klik
          alamat yang tersedia pada daftar
        </span>
      </AddressInfoWrapper>
      <AutocompleteStyled>
        <Autocomplete
          freeSolo
          id='address-suggest'
          disabled={disabled}
          options={suggestAddress}
          getOptionLabel={option => option.name || ''}
          filterOptions={(options, state) => {
            const input = normalizeString(state.inputValue)
            return options.filter(option =>
              normalizeString(option.name).includes(input)
            )
          }}
          value={autoCompleteValue}
          onChange={handleChangeAddress}
          loading={isSuggestLoading}
          renderInput={params => (
            <InputStyled
              required
              {...params}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              label='Kota atau Kecamatan'
              placeholder='Tulis kota/kecamatan'
              size='small'
              onChange={handleChangeKeyword}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password',
              }}
            />
          )}
        />
      </AutocompleteStyled>
      <InputStyled
        fullWidth
        required
        className='postal_code'
        name='postal_code'
        label='Kode Pos'
        variant='outlined'
        size='small'
        type='number'
        InputLabelProps={{ shrink: true }}
        onChange={handleChange}
        value={form?.postal_code}
      />
      <InputStyled
        required
        multiline
        maxRows={4}
        fullWidth
        name='address'
        label='Alamat'
        variant='outlined'
        size='small'
        type='text'
        placeholder='Tulis nama jalan, nomor rumah, nomor kompleks, nama gedung'
        InputLabelProps={{ shrink: true }}
        onChange={handleChange}
        value={form?.address}
      />
      <AddressFormActionStyled>
        <ButtonSecondaryOutlined
          onClick={closeModal}
          disabled={disabled}
          variant='outlined'
        >
          Batal
        </ButtonSecondaryOutlined>
        <ButtonPrimary
          onClick={handleAddAddress}
          disabled={disabled}
          variant='outlined'
        >
          Tambah
        </ButtonPrimary>
      </AddressFormActionStyled>
    </AddressFormStyled>
  )
}

export default memo(AddAddressForm)
